// src/pages/CatalogPage.jsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Form, Row, Col, InputGroup, Button, Container, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getProductsByStatus } from '../services/productApi';

export default function CatalogPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortBy, setSortBy] = useState('');
  // Para la barra de búsqueda
  const [searchTerm, setSearchTerm] = useState('');

  // States for selected filters - NOW ARRAYS FOR MULTI-SELECTION
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [productType, setProductType] = useState('todos');

  // States for available filter options
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);

  // 1. Fetch all (approved) products on page load using getProductsByStatus
  useEffect(() => {
    // Skip API during react-snap pre-rendering
    if (navigator.userAgent === 'ReactSnap') { setLoading(false); return; }
    async function fetchProducts() {
      try {
        setLoading(true);
        // Ajustamos la paginación para obtener más productos
        const response = await getProductsByStatus('APROBADO', 0, 200);
        const fetchedProducts = response.data.content || [];

        setProducts(fetchedProducts);
        // Initially, all products are displayed. Filters will apply in the next useEffect.
        setFilteredProducts(fetchedProducts);

        // Extract unique options for filters
        const uniqueCategories = [...new Set(fetchedProducts.flatMap(p => p.categorias || []))].filter(Boolean).sort();
        const uniqueCountries = [...new Set(fetchedProducts.map(p => p.pais).filter(Boolean))].sort();
        const uniqueSpecialties = [...new Set(fetchedProducts.flatMap(p => p.especialidades || []))].filter(Boolean).sort();

      // Set available filter options
         setAvailableCategories(uniqueCategories);
         setAvailableCountries(uniqueCountries);
         setAvailableSpecialties(uniqueSpecialties);

         // Check URL parameter to auto-select product type filter
         const tipoParam = searchParams.get('tipo');
         if (tipoParam === 'gratis') {
           setProductType('gratis');
         }

       } catch (e) {
        console.error('Error al cargar productos:', e);
        setError('No se pudieron cargar los productos aprobados. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []); // Runs only once on mount

  // Handlers for checkbox changes
  const handleCategoryChange = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getPrice = (p) => {
    const val = p?.precioIndividual ?? p?.price ?? p?.precio ?? p?.unitPrice;
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  const handleProductTypeChange = (type) => {
    setProductType(type);
  };

  const handleCountryChange = (country) => {
    setSelectedCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const handleSpecialtyChange = (specialty) => {
    setSelectedSpecialties(prev =>
      prev.includes(specialty)
        ? prev.filter(s => s !== specialty)
        : [...prev, specialty]
    );
  };

  // Handler para la barra de búsqueda
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // 2. Apply all filters (including search term) whenever products or selected filters change
  useEffect(() => {
    try {
      let currentFiltered = products;

      // Filter by Categories
      if (selectedCategories.length > 0) {
        currentFiltered = currentFiltered.filter(product =>
          product.categorias && selectedCategories.some(cat => product.categorias.includes(cat))
        );
      }

      // Filter by Countries
      if (selectedCountries.length > 0) {
        currentFiltered = currentFiltered.filter(product =>
          selectedCountries.includes(product.pais)
        );
      }

      // Filter by Specialties
      if (selectedSpecialties.length > 0) {
        currentFiltered = currentFiltered.filter(product =>
          product.especialidades && selectedSpecialties.some(spec => product.especialidades.includes(spec))
        );
      }

       // NEW: Filter by Search Term with defensive checks
       if (searchTerm.trim() !== '') {
         const lowercasedSearchTerm = searchTerm.toLowerCase();
         currentFiltered = currentFiltered.filter(product => {
           const productName = product.productName ? product.productName.toLowerCase() : '';
           const descripcion = product.descripcion ? product.descripcion.toLowerCase() : '';
           return productName.includes(lowercasedSearchTerm) || descripcion.includes(lowercasedSearchTerm);
         });
       }

       // Filter by Product Type (Free/Paid/All)
       if (productType === 'gratis') {
         currentFiltered = currentFiltered.filter(product => {
           const price = getPrice(product);
           return price === 0;
         });
       } else if (productType === 'pagos') {
         currentFiltered = currentFiltered.filter(product => {
           const price = getPrice(product);
           return price > 0;
         });
       }

       if (sortBy === 'priceAsc') {
        currentFiltered = [...currentFiltered].sort((a, b) => getPrice(a) - getPrice(b));
      } else if (sortBy === 'priceDesc') {
        currentFiltered = [...currentFiltered].sort((a, b) => getPrice(b) - getPrice(a));
      }

      setFilteredProducts(currentFiltered);
    } catch (e) {
      // Catch any unexpected errors during filtering and prevent the app from crashing
      console.error('Error durante el proceso de filtrado:', e);
      // Muestra un mensaje de error al usuario
      setError('Hubo un error al aplicar los filtros. Por favor, inténtalo de nuevo.');
      setFilteredProducts([]); // Asegura que la página no se queda en blanco
    }
   }, [products, selectedCategories, selectedCountries, selectedSpecialties, searchTerm, sortBy, productType]);

  if (loading) return <div className="text-center py-5">Cargando productos…</div>;
  if (error) return <Alert variant="danger" className="text-center my-5">{error}</Alert>;

  return (
    <Container
      className="catalog-page-container"
    >
      <Helmet>
        <title>Catálogo de Productos AEC | Familias Revit, Modelos 3D y más — AECBlock</title>
        <meta name="description" content="Explora el catálogo completo de AECBlock: familias de Revit, modelos 3D, scripts BIM, plantillas de cálculo y herramientas técnicas para arquitectos e ingenieros." />
        <link rel="canonical" href="https://aecblock.com/catalog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Catálogo de Productos AEC | AECBlock" />
        <meta property="og:description" content="Explora familias de Revit, modelos 3D, scripts BIM y herramientas técnicas para arquitectura e ingeniería." />
        <meta property="og:url" content="https://aecblock.com/catalog" />
        <meta property="og:image" content="https://aecblock.com/default-og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Catálogo de Productos AEC | AECBlock" />
        <meta name="twitter:description" content="Familias Revit, modelos 3D, scripts BIM y más para profesionales AEC." />
      </Helmet>
       <section className="catalog-header-section">
         <div className="container">
           <div className="catalog-header-content">
             <span className="section-label">CATÁLOGO</span>
             <h2 className="section-title">Herramientas listas<br />para usar hoy</h2>
           </div>
         </div>
       </section>
      {/* NEW: Search Bar */}
      <Row className="mb-4 justify-content-center">
        <Col md={8}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Buscar por nombre o descripción..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Button variant="outline-primary" type="button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </Button>
          </InputGroup>
        </Col>
        <Col md={4}>
          <Form.Select
            aria-label="Ordenar por"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="">Ordenar…</option>
            <option value="priceAsc">Precio: menor a mayor</option>
            <option value="priceDesc">Precio: mayor a menor</option>
          </Form.Select>
        </Col>
      </Row>

       {/* Filter Section - Professional Multi-Select Checkboxes */}
       <div className="row mb-4 g-4 align-items-start"> {/* Align items to start for better multi-line checkboxes */}
         {/* Category Filter */}
         <div className="col-md-3">
           <h5 className="mb-3">Categoría</h5>
           <div className="filter-options-scroll"> {/* Add a class for potential scrolling if many options */}
             {availableCategories.map(cat => (
               <div className="form-check" key={cat}>
                 <input
                   className="form-check-input"
                   type="checkbox"
                   value={cat}
                   id={`cat-${cat}`}
                   checked={selectedCategories.includes(cat)}
                   onChange={() => handleCategoryChange(cat)}
                 />
                 <label className="form-check-label" htmlFor={`cat-${cat}`}>
                   {cat}
                 </label>
               </div>
             ))}
           </div>
           {selectedCategories.length > 0 && (
             <Button
               variant="sm"
               className="btn-outline-secondary mt-2"
               onClick={() => setSelectedCategories([])}
             >
               Limpiar Categorías
             </Button>
           )}
         </div>

         {/* Product Type Filter (Free/Paid/All) */}
         <div className="col-md-3">
           <h5 className="mb-3">Tipo</h5>
           <div className="filter-options-scroll">
             <div className="form-check">
               <input
                 className="form-check-input"
                 type="radio"
                 name="productType"
                 id="type-todos"
                 value="todos"
                 checked={productType === 'todos'}
                 onChange={() => handleProductTypeChange('todos')}
               />
               <label className="form-check-label" htmlFor="type-todos">
                 Todos
               </label>
             </div>
             <div className="form-check">
               <input
                 className="form-check-input"
                 type="radio"
                 name="productType"
                 id="type-gratis"
                 value="gratis"
                 checked={productType === 'gratis'}
                 onChange={() => handleProductTypeChange('gratis')}
               />
               <label className="form-check-label" htmlFor="type-gratis">
                 Gratis
               </label>
             </div>
             <div className="form-check">
               <input
                 className="form-check-input"
                 type="radio"
                 name="productType"
                 id="type-pagos"
                 value="pagos"
                 checked={productType === 'pagos'}
                 onChange={() => handleProductTypeChange('pagos')}
               />
               <label className="form-check-label" htmlFor="type-pagos">
                 Pagos
               </label>
             </div>
           </div>
         </div>

         {/* Country Filter */}
         <div className="col-md-3">
          <h5 className="mb-3">País</h5>
          <div className="filter-options-scroll">
            {availableCountries.map(country => (
              <div className="form-check" key={country}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  value={country}
                  id={`country-${country}`}
                  checked={selectedCountries.includes(country)}
                  onChange={() => handleCountryChange(country)}
                />
                <label className="form-check-label" htmlFor={`country-${country}`}>
                  {country}
                </label>
              </div>
            ))}
          </div>
          {selectedCountries.length > 0 && (
            <Button
              variant="sm"
              className="btn-outline-secondary mt-2"
              onClick={() => setSelectedCountries([])}
            >
              Limpiar Países
            </Button>
          )}
        </div>

         {/* Specialty Filter */}
         <div className="col-md-3">
           <h5 className="mb-3">Especialidad</h5>
          <div className="filter-options-scroll">
            {availableSpecialties.map(spec => (
              <div className="form-check" key={spec}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  value={spec}
                  id={`spec-${spec}`}
                  checked={selectedSpecialties.includes(spec)}
                  onChange={() => handleSpecialtyChange(spec)}
                />
                <label className="form-check-label" htmlFor={`spec-${spec}`}>
                  {spec}
                </label>
              </div>
            ))}
          </div>
          {selectedSpecialties.length > 0 && (
            <Button
              variant="sm"
              className="btn-outline-secondary mt-2"
              onClick={() => setSelectedSpecialties([])}
            >
              Limpiar Especialidades
            </Button>
          )}
        </div>
      </div>

      <hr className="my-4" /> {/* Visual separator */}

      {/* Product Grid */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
        {filteredProducts.length > 0 ? (
          filteredProducts.map(product => (
            <Col key={product.idProducto}>
              <ProductCard product={product} />
            </Col>
          ))
        ) : (
          <p className="text-center col-12 text-muted">
            No hay productos que coincidan con tus criterios.
          </p>
        )}
      </div>
    </Container>
  );
}